import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";

export default function AITutor() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const resp = await fetch(`${API}/tutor/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      if (!resp.ok || !resp.body) {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: "Sorry, I could not respond right now." };
          return copy;
        });
        setStreaming(false);
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          if (!p.startsWith("data:")) continue;
          const jsonStr = p.slice(5).trim();
          if (!jsonStr) continue;
          try {
            const obj = JSON.parse(jsonStr);
            if (obj.session_id) setSessionId(obj.session_id);
            if (obj.delta) {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                copy[copy.length - 1] = { ...last, content: last.content + obj.delta };
                return copy;
              });
            }
            if (obj.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: "Error: " + obj.error };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Network error. Try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  if (!user || user === false) return null;

  return (
    <>
      <button
        data-testid="ai-tutor-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#0A0A0A] text-[#FFFFFF] shadow-lg flex items-center justify-center hover:bg-[#000000] transition-colors btn-primary-hover"
        aria-label="Open AI Tutor"
      >
        {open ? <X className="w-5 h-5" /> : <MessageSquare className="w-5 h-5" />}
      </button>

      {open && (
        <div
          data-testid="ai-tutor-panel"
          className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[92vw] h-[520px] max-h-[80vh] bg-[#FFFFFF] border border-[#E5E5E0] shadow-2xl flex flex-col"
        >
          <div className="px-4 py-3 border-b border-[#E5E5E0] flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#C5A059]" strokeWidth={1.75} />
            <div className="font-serif font-semibold text-[#0A0A0A]">Varsity AI Tutor</div>
            <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-[#525252]">Claude</span>
          </div>

          <div ref={scrollRef} data-testid="ai-tutor-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-sm text-[#525252] leading-relaxed">
                <p className="mb-2">Ask me anything about real estate in India or Dubai — RERA, DLD fees, home loans, negotiation, tax.</p>
                <p className="text-xs font-mono uppercase tracking-wider text-[#525252]/70">Try: "What is the DLD fee on a AED 2M apartment?"</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div
                  className={
                    m.role === "user"
                      ? "max-w-[85%] bg-[#0A0A0A] text-[#FFFFFF] px-3 py-2 text-sm rounded-sm"
                      : "max-w-[90%] bg-[#F5F5F0] text-[#0A0A0A] px-3 py-2 text-sm rounded-sm whitespace-pre-wrap"
                  }
                >
                  {m.content || (streaming && i === messages.length - 1 ? <span className="text-[#525252]">...</span> : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-[#E5E5E0] flex items-center gap-2">
            <input
              data-testid="ai-tutor-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask the tutor..."
              className="flex-1 bg-transparent text-sm px-3 py-2 border border-[#E5E5E0] focus:border-[#0A0A0A] focus:outline-none"
              disabled={streaming}
            />
            <button
              data-testid="ai-tutor-send"
              onClick={send}
              disabled={streaming || !input.trim()}
              className="px-3 py-2 bg-[#0A0A0A] text-[#FFFFFF] hover:bg-[#000000] disabled:opacity-40 transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
